/**
 * PageHeading — a page's title with an "i" button that opens what the page is for.
 *
 * Jesse, 2026-10-30: the explanations on Construction Deadlines and Prepare 601
 * were useful but crowded the top of the page. They now live in a dialog behind
 * the "i". The dialog opens by itself the first time someone visits the page, so
 * a new hire still meets it, and after it is closed (button, Escape or a click
 * outside) it stays out of the way until the "i" is pressed.
 *
 * "Seen" is remembered per signed-in user in this browser (localStorage: it has
 * to outlast the tab, unlike the filter memory in useSessionState). It holds
 * nothing but a flag. Raise `version` when the text changes enough that everyone
 * should see it again.
 */
import { useEffect, useState } from 'react'
import Modal from './Modal'
import { InfoCircleIcon } from './Icons'
import { useAuth } from '../services/auth'

export interface PageHelp {
  /** Stable id for the "seen" flag, e.g. "constructionDeadlines". */
  id: string
  /** Bump to show the explainer to everyone once more. */
  version: number
  /** Dialog title, e.g. "About Construction Deadlines". */
  title: string
  content: React.ReactNode
}

interface PageHeadingProps {
  title: React.ReactNode
  help: PageHelp
  /** A line under the title (a count, a one-line summary). */
  subtitle?: React.ReactNode
  /** Extra classes for the heading row, e.g. "print:hidden". */
  className?: string
  /** Anything to the right of the title (buttons, links). */
  children?: React.ReactNode
}

function seenKey(help: PageHelp, userId: number | undefined) {
  return `pageHelp.${help.id}.v${help.version}.${userId ?? 'anon'}`
}

function hasSeen(key: string): boolean {
  try {
    return localStorage.getItem(key) === '1'
  } catch {
    // Storage unavailable: don't nag on every visit.
    return true
  }
}

function markSeen(key: string) {
  try {
    localStorage.setItem(key, '1')
  } catch {
    // Unavailable: it will simply open again next time.
  }
}

export default function PageHeading({
  title, help, subtitle, className = '', children,
}: PageHeadingProps) {
  const { user } = useAuth()
  const key = seenKey(help, user?.id)
  const [open, setOpen] = useState(false)

  // Decide once the user is known, so the flag is read under the right name.
  // Marked seen as it opens, not as it closes: following a link inside it leaves
  // the page without closing it, and that still counts as having seen it.
  useEffect(() => {
    if (user && !hasSeen(key)) {
      markSeen(key)
      setOpen(true)
    }
  }, [user, key])

  const close = () => setOpen(false)

  const titleId = `page-help-${help.id}`

  return (
    <div className={`flex flex-wrap items-center justify-between gap-2 ${className}`}>
      <div>
      <div className="flex items-center gap-1.5">
        {/* One title size for every page that uses this. */}
        <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label={help.title}
          title={help.title}
          className="tap-icon rounded-full text-gray-400 hover:text-primary-600 outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
        >
          <InfoCircleIcon className="h-5 w-5" />
        </button>
      </div>
      {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
      </div>
      {children}
      {open && (
        <Modal titleId={titleId} title={help.title} onClose={close} showCloseButton maxWidth="max-w-xl">
          <div className="space-y-3 text-sm text-gray-700 leading-relaxed">{help.content}</div>
          <div className="mt-5 flex justify-end">
            <button type="button" onClick={close} className="btn btn-primary">
              Got it
            </button>
          </div>
        </Modal>
      )}
    </div>
  )
}

/** A bulleted list for explainer content, so every page's help reads alike. */
export function HelpList({ children }: { children: React.ReactNode }) {
  return <ul className="list-disc space-y-1.5 pl-5">{children}</ul>
}
